import {
  Button,
  Checkbox,
  Modal,
  NumberInput,
  Select,
  Skeleton,
  Stack,
  Textarea,
} from '@mantine/core';
import {
  IconArrowsExchange,
  IconBuildingStore,
  IconCheck,
  IconFileInvoice,
  IconFileTypePdf,
  IconLock,
  IconPointFilled,
  IconSend,
  IconUserShield,
  IconX,
} from '@tabler/icons-react';
import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../EmptyState.js';
import { StatusBadge } from '../../StatusBadge.js';
import table from '../materials/Materials.module.css';
import { errorCode } from '../personnel/source.js';
import {
  type AwardDraft,
  awardTone,
  changeText,
  draftProposals,
  initialDraft,
  lineOptions,
  liveAward,
  supplierLocked,
} from './award-draft.js';
import { PasswordDialog } from './PasswordDialog.js';
import classes from './Purchasing.module.css';
import { formatStampLocal } from './requisitions.js';
import { shortHash } from './rfqs.js';
import type { RfqAwardSource } from './source.js';
import { LoadError } from './table.js';
import type {
  AwardLine,
  AwardRoundDiff,
  AwardStatus,
  AwardTermField,
  AwardTerms,
  RfqAward,
  RfqAwards,
  RfqDetail,
} from './types.js';

/**
 * RFQ ▸ Award (ADR 0111 §D; plan §3/D "Ofis"): satırları tedarikçilere böl, öneri gönder, tur
 * geçmişi ve fark vurgusu (eklenen `success`, çıkarılan `danger` üstü çizili, miktar ve fiyat
 * değişimi `warning` "100 → 150"), karşı teklifi kabul et, kesinleştir (dört göz; PO açılır),
 * iptal et, yorum yaz. Yorumlar ve turlar değiştirilemez.
 */

const useK = () => {
  const { t } = useTranslation();
  return (key: string, options: Record<string, unknown> = {}) =>
    t(`purchasing.rfq.detail.award.${key}`, options);
};

type Money = (value: number | null) => string;
type Quantity = (value: number) => string;

export function AwardStatusBadge({ status }: { status: AwardStatus }) {
  const k = useK();
  return (
    <StatusBadge tone={awardTone(status)} withIcon>
      {k(`status.${status}`)}
    </StatusBadge>
  );
}

const MONEY_TERMS: readonly AwardTermField[] = [
  'discount',
  'freight',
  'customs',
  'tax',
  'serviceFee',
];

function termValue(
  field: AwardTermField,
  value: unknown,
  money: Money,
  k: ReturnType<typeof useK>,
): string {
  if (value === null || value === undefined || value === '') return '—';
  if (field === 'leadTimeDays') return k('terms.days', { count: Number(value) });
  if (MONEY_TERMS.includes(field)) return money(Number(value));
  return String(value);
}

/**
 * Turun önceki tura göre farkı: eklenen satır `success`, çıkarılan `danger` ve üstü çizili,
 * miktarı ya da fiyatı değişen `warning` ("100 → 150"; fiyat kalın); koşullar ayrı madde.
 */
export function RoundDiffList({
  diff,
  names,
  money,
  quantity,
}: {
  diff: AwardRoundDiff;
  names: ReadonlyMap<string, string>;
  money: Money;
  quantity: Quantity;
}) {
  const k = useK();
  const shown = diff.lines.filter((line) => line.change !== 'unchanged');
  if (shown.length === 0 && diff.terms.length === 0) {
    return <p className={classes.awardNote}>{k('diff.none')}</p>;
  }
  const lineName = (line: (typeof shown)[number]) => {
    const value = line.next ?? line.previous;
    return value?.substitute?.name ?? names.get(line.rfqLineId) ?? '';
  };
  return (
    <ul className={classes.diffList}>
      {shown.map((line) => (
        <li key={line.rfqLineId} className={classes.diffItem} data-change={line.change}>
          {line.change === 'added' ? (
            <StatusBadge tone="success">{k('diff.added')}</StatusBadge>
          ) : line.change === 'removed' ? (
            <StatusBadge tone="danger">{k('diff.removed')}</StatusBadge>
          ) : (
            <StatusBadge tone="warning">{k('diff.changed')}</StatusBadge>
          )}
          <span className={classes.diffName} data-removed={line.change === 'removed' || undefined}>
            {lineName(line)}
          </span>
          {line.change === 'added' && line.next ? (
            <span className={classes.muted}>
              {quantity(line.next.quantity)} × {money(line.next.unitPrice)}
            </span>
          ) : null}
          {line.quantity ? (
            <span className={classes.diffDelta} data-dir={deltaDir(line.quantity)}>
              {k('diff.qty', { change: changeText(line.quantity, quantity) })}
            </span>
          ) : null}
          {line.unitPrice ? (
            <span className={`${classes.diffDelta} ${classes.diffPrice}`}>
              {k('diff.price', { change: changeText(line.unitPrice, (v) => money(v)) })}
            </span>
          ) : null}
          {line.substitute ? <span className={classes.muted}>{k('diff.substitute')}</span> : null}
          {line.note ? <span className={classes.muted}>{k('diff.note')}</span> : null}
        </li>
      ))}
      {diff.terms.map((term) => (
        <li key={term.field} className={classes.diffItem} data-change="terms">
          <StatusBadge tone="warning">{k(`terms.${term.field}`)}</StatusBadge>
          <span className={classes.diffDelta}>
            {`${termValue(term.field, term.from, money, k)} → ${termValue(term.field, term.to, money, k)}`}
          </span>
        </li>
      ))}
    </ul>
  );
}

function deltaDir(change: { from: number | null; to: number | null }) {
  if (change.from === null || change.to === null) return undefined;
  return change.to > change.from ? 'up' : change.to < change.from ? 'down' : undefined;
}

function Box({ label, children }: { label: string; children: ReactNode }) {
  return (
    <section
      className={`${table.tableWrap} ${classes.boxed}`}
      aria-label={label}
      // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
      tabIndex={0}
    >
      <table className={`${table.table} ${classes.table}`}>{children}</table>
    </section>
  );
}

/** Satır bölmesi: boştaki her kalem için tedarikçi ve fiyat; tedarikçi başına "Send proposal". */
function SplitGrid({
  detail,
  awards,
  money,
  quantity,
  onSend,
}: {
  detail: RfqDetail;
  awards: RfqAwards;
  money: Money;
  quantity: Quantity;
  onSend: (entry: ReturnType<typeof draftProposals>[number]) => Promise<string | null>;
}) {
  const k = useK();
  const { t } = useTranslation();
  const [draft, setDraft] = useState<AwardDraft>(() => initialDraft(detail, awards));
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => setDraft(initialDraft(detail, awards)), [detail, awards]);
  const free = awards.lines.filter((slot) => slot.awardId === null);
  const proposals = draftProposals(detail, awards, draft, message.trim() || null);
  const set = (rfqLineId: string, patch: Partial<AwardDraft[string]>) =>
    setDraft((d) => ({
      ...d,
      [rfqLineId]: {
        ...(d[rfqLineId] ?? { optionKey: null, quantity: null, unitPrice: null }),
        ...patch,
      },
    }));
  return (
    <section className={classes.awardSplit} aria-labelledby="award-split">
      <h3 id="award-split" className={classes.subTitle}>
        {k('split.title')}
      </h3>
      <p className={classes.note}>{k('split.note')}</p>
      <Box label={k('split.title')}>
        <thead>
          <tr>
            <th scope="col" className={classes.index}>
              {k('lines.no')}
            </th>
            <th scope="col" className={classes.key}>
              {k('split.item')}
            </th>
            <th scope="col" data-numeric>
              {k('split.requested')}
            </th>
            <th scope="col">{k('split.supplier')}</th>
            <th scope="col" data-numeric>
              {k('split.qty')}
            </th>
            <th scope="col" data-numeric>
              {k('split.unitPrice')}
            </th>
            <th scope="col" data-numeric>
              {k('split.total')}
            </th>
          </tr>
        </thead>
        <tbody>
          {awards.lines.map((slot) => {
            const row = draft[slot.rfqLineId];
            const options = lineOptions(detail, slot.rfqLineId);
            const chosen = options.find((o) => o.key === row?.optionKey);
            const lineTotal =
              row?.quantity != null && row.unitPrice != null ? row.quantity * row.unitPrice : null;
            return (
              <tr key={slot.rfqLineId} data-awarded={slot.awardId !== null || undefined}>
                <td className={classes.index}>{slot.lineNo}</td>
                <td className={`${classes.wrap} ${classes.key}`}>
                  {slot.name}
                  {chosen?.substitute ? (
                    <span className={classes.awardSub}>
                      <StatusBadge tone="info" icon={<IconArrowsExchange size={11} />}>
                        {k('split.substitute')}
                      </StatusBadge>
                      <span className={classes.cellSubName}>{chosen.substitute.name}</span>
                    </span>
                  ) : null}
                </td>
                <td data-numeric>
                  {quantity(slot.quantity)} {slot.unit}
                </td>
                {slot.awardId !== null ? (
                  <td colSpan={4}>
                    <StatusBadge tone="neutral" icon={<IconLock size={11} />}>
                      {k('split.awardedTo', { supplier: slot.supplierName ?? '' })}
                    </StatusBadge>
                  </td>
                ) : options.length === 0 ? (
                  <td colSpan={4} className={classes.muted}>
                    {k('split.noOffers')}
                  </td>
                ) : (
                  <>
                    <td>
                      <Select
                        aria-label={`${k('split.supplier')} · ${slot.name}`}
                        size="xs"
                        value={row?.optionKey ?? ''}
                        data={[
                          { value: '', label: k('split.none') },
                          ...options.map((o) => {
                            const locked = supplierLocked(awards, o.supplierId);
                            const tags = [
                              o.best ? k('split.best') : null,
                              o.kind === 'substitute' ? k('split.substitute') : null,
                            ].filter(Boolean);
                            return {
                              value: o.key,
                              disabled: locked,
                              label: locked
                                ? k('split.locked', { supplier: o.supplierName })
                                : `${o.supplierName} · ${money(o.unitPrice)}${tags.length ? ` · ${tags.join(', ')}` : ''}`,
                            };
                          }),
                        ]}
                        allowDeselect={false}
                        onChange={(value) => {
                          const option = options.find((o) => o.key === value);
                          set(
                            slot.rfqLineId,
                            option
                              ? {
                                  optionKey: option.key,
                                  quantity: option.quantity,
                                  unitPrice: option.unitPrice,
                                }
                              : { optionKey: null },
                          );
                        }}
                        comboboxProps={{ withinPortal: true }}
                      />
                    </td>
                    <td data-numeric>
                      <NumberInput
                        aria-label={`${k('split.qty')} · ${slot.name}`}
                        size="xs"
                        min={0}
                        decimalScale={3}
                        hideControls
                        disabled={!row?.optionKey}
                        value={row?.quantity ?? ''}
                        onChange={(value) =>
                          set(slot.rfqLineId, {
                            quantity: typeof value === 'number' ? value : null,
                          })
                        }
                        className={classes.awardNumber}
                      />
                    </td>
                    <td data-numeric>
                      <NumberInput
                        aria-label={`${k('split.unitPrice')} · ${slot.name}`}
                        size="xs"
                        min={0}
                        decimalScale={4}
                        hideControls
                        disabled={!row?.optionKey}
                        value={row?.unitPrice ?? ''}
                        onChange={(value) =>
                          set(slot.rfqLineId, {
                            unitPrice: typeof value === 'number' ? value : null,
                          })
                        }
                        className={classes.awardNumber}
                      />
                    </td>
                    <td data-numeric>{money(lineTotal)}</td>
                  </>
                )}
              </tr>
            );
          })}
        </tbody>
      </Box>
      {free.length === 0 ? (
        <p className={classes.awardNote}>{k('split.allAwarded')}</p>
      ) : (
        <>
          <Textarea
            label={k('split.message')}
            value={message}
            onChange={(event) => setMessage(event.currentTarget.value)}
            autosize
            minRows={1}
            maxLength={1000}
            className={classes.awardMessage}
          />
          <ul className={classes.awardSend} aria-label={k('split.send')}>
            {proposals.map((entry) => (
              <li key={entry.supplierId}>
                <span className={classes.awardSendWho}>
                  <IconBuildingStore size={16} aria-hidden />
                  <strong>{entry.supplierName}</strong>
                  <span className={classes.muted}>
                    {k('split.summary', {
                      count: entry.added,
                      total: `${money(entry.itemsTotal)} ${detail.currency}`,
                    })}
                  </span>
                </span>
                <Button
                  size="xs"
                  leftSection={<IconSend size={14} />}
                  loading={busy === entry.supplierId}
                  onClick={async () => {
                    setBusy(entry.supplierId);
                    setError(null);
                    const code = await onSend(entry);
                    setBusy(null);
                    if (code) setError(code);
                  }}
                >
                  {entry.award
                    ? k('split.addToAward', { no: entry.award.roundNo + 1 })
                    : k('split.send')}
                </Button>
              </li>
            ))}
          </ul>
        </>
      )}
      {error ? (
        <p className={classes.error} role="alert">
          {t(`purchasing.errors.${error}`, { defaultValue: t('purchasing.errors.GENERIC') })}
        </p>
      ) : null}
    </section>
  );
}

/** Ödülün geçerli satırları; son turdaki değişiklik satırda vurgulu. */
function AwardLines({
  award,
  money,
  quantity,
  names,
}: {
  award: RfqAward;
  money: Money;
  quantity: Quantity;
  names: ReadonlyMap<string, string>;
}) {
  const k = useK();
  const last = award.rounds.at(-1);
  const changes = new Map(
    (last && award.rounds.length > 1 ? last.diff.lines : []).map((d) => [d.rfqLineId, d]),
  );
  const removed = (last && award.rounds.length > 1 ? last.diff.lines : []).filter(
    (d) => d.change === 'removed',
  );
  return (
    <Box label={`${award.supplierName} · ${k('lines.item')}`}>
      <thead>
        <tr>
          <th scope="col" className={classes.index}>
            {k('lines.no')}
          </th>
          <th scope="col" className={classes.key}>
            {k('lines.item')}
          </th>
          <th scope="col" data-numeric>
            {k('lines.qty')}
          </th>
          <th scope="col">{k('lines.uom')}</th>
          <th scope="col" data-numeric>
            {k('lines.unitPrice')}
          </th>
          <th scope="col" data-numeric>
            {k('lines.total')}
          </th>
        </tr>
      </thead>
      <tbody>
        {award.lines.map((line) => {
          const change = changes.get(line.rfqLineId);
          return (
            <tr key={line.rfqLineId} data-change={change?.change}>
              <td className={classes.index}>{line.lineNo ?? ''}</td>
              <td className={`${classes.wrap} ${classes.key}`}>
                {line.substitute ? line.substitute.name : line.name}
                {line.substitute ? (
                  <span className={classes.awardSub}>
                    <StatusBadge tone="info" icon={<IconArrowsExchange size={11} />}>
                      {k('lines.substituteFor', { name: line.name })}
                    </StatusBadge>
                  </span>
                ) : null}
                {change?.change === 'added' ? (
                  <span className={classes.flags}>
                    <StatusBadge tone="success">{k('diff.added')}</StatusBadge>
                  </span>
                ) : null}
                {line.note ? <span className={classes.awardLineNote}>{line.note}</span> : null}
              </td>
              <td data-numeric>
                {change?.quantity ? (
                  <span className={classes.diffDelta} data-dir={deltaDir(change.quantity)}>
                    {changeText(change.quantity, quantity)}
                  </span>
                ) : (
                  quantity(line.quantity)
                )}
              </td>
              <td>{line.unit}</td>
              <td data-numeric>
                {change?.unitPrice ? (
                  <span className={`${classes.diffDelta} ${classes.diffPrice}`}>
                    {changeText(change.unitPrice, (v) => money(v))}
                  </span>
                ) : (
                  money(line.unitPrice)
                )}
              </td>
              <td data-numeric>{money(line.total)}</td>
            </tr>
          );
        })}
        {removed.map((d) => (
          <tr key={`removed-${d.rfqLineId}`} data-change="removed">
            <td className={classes.index} />
            <td className={`${classes.wrap} ${classes.key}`}>
              <span className={classes.diffName} data-removed>
                {d.previous?.substitute?.name ?? names.get(d.rfqLineId) ?? ''}
              </span>
              <span className={classes.flags}>
                <StatusBadge tone="danger">{k('diff.removed')}</StatusBadge>
              </span>
            </td>
            <td data-numeric>{d.previous ? quantity(d.previous.quantity) : ''}</td>
            <td />
            <td data-numeric>{d.previous ? money(d.previous.unitPrice) : ''}</td>
            <td data-numeric>0</td>
          </tr>
        ))}
      </tbody>
    </Box>
  );
}

function TermsList({
  terms,
  award,
  money,
}: {
  terms: AwardTerms | null;
  award: RfqAward;
  money: Money;
}) {
  const k = useK();
  const fields: AwardTermField[] = [
    'leadTimeDays',
    'deliveryLocation',
    'paymentTerms',
    'warranty',
    'freight',
    'discount',
  ];
  return (
    <dl className={classes.awardTerms}>
      {fields.map((field) => (
        <div key={field}>
          <dt>{k(`terms.${field}`)}</dt>
          <dd>{termValue(field, terms?.[field], money, k)}</dd>
        </div>
      ))}
      <div>
        <dt>{k('terms.itemsTotal')}</dt>
        <dd>{money(award.itemsTotal)}</dd>
      </div>
      <div data-strong>
        <dt>{k('terms.total', { currency: award.currency })}</dt>
        <dd>{money(award.total)}</dd>
      </div>
    </dl>
  );
}

/** Tur geçmişi: Rev, taraf, işlem, kişi, zaman, toplam, hash ve önceki tura göre fark. */
function RoundHistory({
  award,
  money,
  quantity,
  names,
}: {
  award: RfqAward;
  money: Money;
  quantity: Quantity;
  names: ReadonlyMap<string, string>;
}) {
  const k = useK();
  return (
    <section aria-label={`${k('history.title')} · ${award.supplierName}`}>
      <h4 className={classes.awardSubhead}>{k('history.title')}</h4>
      <ol className={classes.roundList}>
        {[...award.rounds].reverse().map((round) => (
          <li key={round.roundNo} className={classes.round} data-party={round.party}>
            <div className={classes.roundHead}>
              <strong>{k('round', { no: round.roundNo })}</strong>
              <StatusBadge tone={round.party === 'supplier' ? 'info' : 'neutral'}>
                {k(`history.action.${round.action}`)}
              </StatusBadge>
              <span className={classes.muted}>
                {k('history.by', {
                  party: k(`history.party.${round.party}`),
                  name: round.byName,
                })}
              </span>
              <span className={classes.muted}>{formatStampLocal(round.at)}</span>
              <span className={classes.roundTotal}>
                {money(round.total)} {award.currency}
              </span>
              <code className={classes.hash} title={`${k('history.hash')}: ${round.contentHash}`}>
                {shortHash(round.contentHash)}
              </code>
            </div>
            {round.message ? <p className={classes.roundMessage}>{round.message}</p> : null}
            {round.roundNo === 1 ? (
              <p className={classes.awardNote}>{k('diff.first')}</p>
            ) : (
              <RoundDiffList diff={round.diff} names={names} money={money} quantity={quantity} />
            )}
          </li>
        ))}
      </ol>
    </section>
  );
}

function Comments({
  award,
  onSend,
}: {
  award: RfqAward;
  onSend: (body: string) => Promise<string | null>;
}) {
  const k = useK();
  const { t } = useTranslation();
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  return (
    <section aria-label={`${k('comments.title')} · ${award.supplierName}`}>
      <h4 className={classes.awardSubhead}>{k('comments.title')}</h4>
      {award.comments.length === 0 ? (
        <p className={classes.awardNote}>{k('comments.empty')}</p>
      ) : (
        <ul className={classes.awardComments}>
          {award.comments.map((comment) => (
            <li key={comment.id} className={classes.awardComment} data-party={comment.party}>
              <p className={classes.commentRole}>
                {comment.party === 'supplier'
                  ? `${k('comments.supplier')} · ${comment.byName ?? award.supplierName}`
                  : `${k('comments.office')} · ${comment.byName ?? ''}`}
                <span className={classes.muted}> · {formatStampLocal(comment.at)}</span>
              </p>
              <p className={classes.commentBody}>{comment.body}</p>
            </li>
          ))}
        </ul>
      )}
      <p className={classes.awardNote}>
        <IconLock size={12} aria-hidden /> {k('comments.immutable')}
      </p>
      {award.can.comment ? (
        <form
          className={classes.awardCommentForm}
          onSubmit={async (event) => {
            event.preventDefault();
            if (!body.trim()) return;
            setBusy(true);
            setError(null);
            const code = await onSend(body.trim());
            setBusy(false);
            if (code) setError(code);
            else setBody('');
          }}
        >
          <Textarea
            aria-label={k('comments.placeholder')}
            placeholder={k('comments.placeholder')}
            value={body}
            onChange={(event) => setBody(event.currentTarget.value)}
            autosize
            minRows={2}
            maxLength={2000}
          />
          <Button type="submit" size="xs" variant="light" loading={busy} disabled={!body.trim()}>
            {k('comments.send')}
          </Button>
          {error ? (
            <p className={classes.error} role="alert">
              {t(`purchasing.errors.${error}`, { defaultValue: t('purchasing.errors.GENERIC') })}
            </p>
          ) : null}
        </form>
      ) : null}
    </section>
  );
}

/** Ödül kartı: başlık, eylemler, satırlar (son değişiklik vurgulu), koşullar, geçmiş, belgeler, yorumlar. */
export function AwardCard({
  award,
  money,
  quantity,
  names,
  onAction,
  onOpenOrder,
  onComment,
}: {
  award: RfqAward;
  money: Money;
  quantity: Quantity;
  names: ReadonlyMap<string, string>;
  onAction: (action: 'acceptCounter' | 'propose' | 'finalize' | 'cancel', award: RfqAward) => void;
  onOpenOrder?: (purchaseOrderId: string) => void;
  onComment: (body: string) => Promise<string | null>;
}) {
  const k = useK();
  return (
    <article
      className={classes.awardCard}
      aria-label={award.supplierName}
      data-status={award.status}
    >
      <header className={classes.awardHead}>
        <div className={classes.awardTitle}>
          <h3>{award.supplierName}</h3>
          <AwardStatusBadge status={award.status} />
          {award.kind === 'direct' ? (
            // Portal turu olmadan ofisin satır seçimi; PO onay zincirindedir (ADR 0166 §2)
            <StatusBadge tone="info">{k('direct')}</StatusBadge>
          ) : (
            <span className={classes.muted}>{k('round', { no: award.roundNo })}</span>
          )}
        </div>
        <div className={classes.headerActions}>
          {award.purchaseOrder && onOpenOrder ? (
            <Button
              size="xs"
              variant="default"
              leftSection={<IconFileInvoice size={14} />}
              onClick={() => onOpenOrder(award.purchaseOrder?.id ?? '')}
            >
              {k('actions.openOrder', { no: award.purchaseOrder.poNo })}
            </Button>
          ) : null}
          {award.can.cancel ? (
            <Button
              size="xs"
              variant="default"
              leftSection={<IconX size={14} />}
              onClick={() => onAction('cancel', award)}
            >
              {k('actions.cancel')}
            </Button>
          ) : null}
          {award.can.propose ? (
            <Button
              size="xs"
              variant="default"
              leftSection={<IconSend size={14} />}
              onClick={() => onAction('propose', award)}
            >
              {k('actions.propose')}
            </Button>
          ) : null}
          {award.can.acceptCounter ? (
            <Button
              size="xs"
              variant="light"
              leftSection={<IconCheck size={14} />}
              onClick={() => onAction('acceptCounter', award)}
            >
              {k('actions.acceptCounter')}
            </Button>
          ) : null}
          {award.can.finalize || award.can.fourEyes ? (
            <Button
              size="xs"
              leftSection={<IconPointFilled size={12} />}
              disabled={!award.can.finalize}
              aria-describedby={award.can.fourEyes ? `four-eyes-${award.id}` : undefined}
              onClick={() => onAction('finalize', award)}
            >
              {k('actions.finalize')}
            </Button>
          ) : null}
        </div>
      </header>
      {award.can.fourEyes ? (
        <p className={classes.notice} data-tone="info" id={`four-eyes-${award.id}`}>
          <IconUserShield size={14} aria-hidden /> {k('actions.fourEyes')}
        </p>
      ) : null}
      {award.cancelReason ? (
        <p className={classes.notice} data-tone="danger">
          {award.cancelReason}
        </p>
      ) : null}
      {award.selectionReason ? (
        <p className={classes.notice} data-tone="info">
          {k('selectionReason', { reason: award.selectionReason })}
        </p>
      ) : null}
      <div className={classes.awardBody}>
        <div className={classes.awardMain}>
          <AwardLines award={award} money={money} quantity={quantity} names={names} />
          <RoundHistory award={award} money={money} quantity={quantity} names={names} />
        </div>
        <aside className={classes.awardSide}>
          <h4 className={classes.awardSubhead}>{k('terms.title')}</h4>
          <TermsList terms={award.terms} award={award} money={money} />
          {award.documents.length > 0 ? (
            <section aria-label={k('documents.title')}>
              <h4 className={classes.awardSubhead}>{k('documents.title')}</h4>
              <ul className={classes.awardDocs}>
                {award.documents.map((doc) => (
                  <li key={doc.id}>
                    <IconFileTypePdf size={16} aria-hidden />
                    <span className={classes.awardDocName}>{doc.fileName}</span>
                    <code className={classes.hash} title={doc.sha256}>
                      {k('documents.sha', { hash: shortHash(doc.sha256) })}
                    </code>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}
          <Comments award={award} onSend={onComment} />
        </aside>
      </div>
    </article>
  );
}

/** Var olan ödüle yeni öneri: miktar, fiyat, çıkarma ve koşullar. */
function ProposalDialog({
  award,
  onClose,
  onSend,
}: {
  award: RfqAward;
  onClose: () => void;
  onSend: (proposal: {
    lines: AwardLine[];
    terms: AwardTerms;
    message: string | null;
  }) => Promise<string | null>;
}) {
  const k = useK();
  const { t } = useTranslation();
  const [lines, setLines] = useState(() =>
    award.lines.map((line) => ({ ...line, removed: false })),
  );
  const [terms, setTerms] = useState<AwardTerms>(
    () =>
      award.terms ?? {
        leadTimeDays: null,
        deliveryLocation: null,
        deliveryTerms: null,
        paymentTerms: null,
        warranty: null,
        discount: 0,
        freight: 0,
        customs: 0,
        tax: 0,
        serviceFee: 0,
        remarks: null,
      },
  );
  const [message, setMessage] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const kept = lines.filter((line) => !line.removed);
  return (
    <Modal
      opened
      onClose={onClose}
      title={k('proposal.title', { supplier: award.supplierName })}
      size="xl"
      closeButtonProps={{ 'aria-label': t('purchasing.password.back') }}
    >
      <form
        onSubmit={async (event) => {
          event.preventDefault();
          if (kept.length === 0) {
            setError('NO_LINES');
            return;
          }
          setBusy(true);
          setError(null);
          const code = await onSend({
            lines: kept.map(({ removed: _removed, ...line }) => ({
              rfqLineId: line.rfqLineId,
              quantity: line.quantity,
              unitPrice: line.unitPrice,
              substitute: line.substitute,
              note: line.note,
            })),
            terms,
            message: message.trim() || null,
          });
          setBusy(false);
          if (code) setError(code);
        }}
      >
        <Stack gap="sm">
          <p className={classes.awardNote}>{k('proposal.body')}</p>
          <ul className={classes.proposalLines}>
            {lines.map((line, index) => (
              <li key={line.rfqLineId} data-removed={line.removed || undefined}>
                <span className={classes.proposalName}>
                  {line.substitute ? line.substitute.name : line.name}
                </span>
                <NumberInput
                  label={k('lines.qty')}
                  size="xs"
                  min={0}
                  decimalScale={3}
                  hideControls
                  disabled={line.removed}
                  value={line.quantity}
                  onChange={(value) =>
                    setLines((list) =>
                      list.map((l, i) =>
                        i === index ? { ...l, quantity: typeof value === 'number' ? value : 0 } : l,
                      ),
                    )
                  }
                />
                <NumberInput
                  label={k('lines.unitPrice')}
                  size="xs"
                  min={0}
                  decimalScale={4}
                  hideControls
                  disabled={line.removed}
                  value={line.unitPrice}
                  onChange={(value) =>
                    setLines((list) =>
                      list.map((l, i) =>
                        i === index
                          ? { ...l, unitPrice: typeof value === 'number' ? value : 0 }
                          : l,
                      ),
                    )
                  }
                />
                <Checkbox
                  label={k('proposal.remove')}
                  checked={line.removed}
                  onChange={(event) => {
                    const removed = event.currentTarget.checked;
                    setLines((list) => list.map((l, i) => (i === index ? { ...l, removed } : l)));
                  }}
                />
              </li>
            ))}
          </ul>
          <div className={classes.proposalTerms}>
            <NumberInput
              label={k('terms.leadTimeDays')}
              size="xs"
              min={0}
              allowDecimal={false}
              value={terms.leadTimeDays ?? ''}
              onChange={(value) =>
                setTerms((v) => ({ ...v, leadTimeDays: typeof value === 'number' ? value : null }))
              }
            />
            {(['freight', 'discount'] as const).map((field) => (
              <NumberInput
                key={field}
                label={k(`terms.${field}`)}
                size="xs"
                min={0}
                decimalScale={2}
                value={terms[field]}
                onChange={(value) =>
                  setTerms((v) => ({ ...v, [field]: typeof value === 'number' ? value : 0 }))
                }
              />
            ))}
          </div>
          <Textarea
            label={k('proposal.message')}
            value={message}
            onChange={(event) => setMessage(event.currentTarget.value)}
            autosize
            minRows={2}
            maxLength={1000}
          />
          {error ? (
            <p className={classes.error} role="alert">
              {error === 'NO_LINES'
                ? k('proposal.noLines')
                : t(`purchasing.errors.${error}`, { defaultValue: t('purchasing.errors.GENERIC') })}
            </p>
          ) : null}
        </Stack>
        <div className={classes.dialogActions}>
          <Button variant="default" onClick={onClose}>
            {t('purchasing.password.back')}
          </Button>
          <Button type="submit" loading={busy} leftSection={<IconSend size={16} />}>
            {k('proposal.send')}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

type AwardDialog = {
  kind: 'acceptCounter' | 'propose' | 'finalize' | 'cancel';
  award: RfqAward;
} | null;

/** "Award" sekmesi: bölme ızgarası ve ödül kartları. */
export function RfqAwardsPanel({
  detail,
  source,
  money,
  quantity,
  onOpenOrder,
  onChanged,
}: {
  detail: RfqDetail;
  source: RfqAwardSource;
  money: Money;
  quantity: Quantity;
  onOpenOrder?: (purchaseOrderId: string) => void;
  /** Ödül değişince RFQ kartı yenilenir (seçim ve iptal bayrakları). */
  onChanged?: () => void;
}) {
  const k = useK();
  const [awards, setAwards] = useState<RfqAwards | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [dialog, setDialog] = useState<AwardDialog>(null);
  const load = useCallback(async () => {
    setError(null);
    try {
      setAwards(await source.get(detail.id));
    } catch (failure) {
      setError(errorCode(failure));
    }
  }, [source, detail.id]);
  useEffect(() => {
    void load();
  }, [load]);
  const names = useMemo(
    () => new Map(detail.lines.map((line) => [line.id, line.name])),
    [detail.lines],
  );
  if (error) return <LoadError title={k('error')} code={error} onRetry={() => void load()} />;
  if (!awards) {
    return (
      <div aria-busy>
        <span className={classes.visuallyHidden}>{k('loading')}</span>
        <Skeleton height={180} />
      </div>
    );
  }
  const run = async (action: () => Promise<RfqAwards>, done: string) => {
    try {
      setAwards(await action());
      setDialog(null);
      setNotice(done);
      onChanged?.();
      return null;
    } catch (failure) {
      return errorCode(failure);
    }
  };
  const shown = awards.awards.filter((a) => a.status !== 'draft');
  return (
    <div className={classes.awardPanel}>
      <p className={classes.note}>{k('intro')}</p>
      {notice ? (
        <p className={classes.notice} data-tone="success" role="status">
          {notice}
        </p>
      ) : null}
      {awards.can.create ? (
        <SplitGrid
          detail={detail}
          awards={awards}
          money={money}
          quantity={quantity}
          onSend={(entry) =>
            run(
              () =>
                entry.award
                  ? source.propose(entry.award.id, {
                      roundNo: entry.award.roundNo,
                      proposal: entry.proposal,
                    })
                  : source.create(detail.id, {
                      supplierId: entry.supplierId,
                      proposal: entry.proposal,
                    }),
              k(entry.award ? 'notices.proposed' : 'notices.created', {
                supplier: entry.supplierName,
              }),
            )
          }
        />
      ) : null}
      {shown.length === 0 ? (
        <EmptyState
          title={k('empty')}
          description={awards.can.create ? k('emptyText') : undefined}
        />
      ) : (
        <div className={classes.awardCards}>
          {shown
            .filter(liveAward)
            .concat(shown.filter((a) => !liveAward(a)))
            .map((award) => (
              <AwardCard
                key={award.id}
                award={award}
                money={money}
                quantity={quantity}
                names={names}
                onAction={(kind, target) => setDialog({ kind, award: target })}
                {...(onOpenOrder ? { onOpenOrder } : {})}
                onComment={(body) =>
                  run(() => source.comment(award.id, { body }), k('notices.commented'))
                }
              />
            ))}
        </div>
      )}
      {dialog?.kind === 'propose' ? (
        <ProposalDialog
          award={dialog.award}
          onClose={() => setDialog(null)}
          onSend={(proposal) =>
            run(
              () => source.propose(dialog.award.id, { roundNo: dialog.award.roundNo, proposal }),
              k('notices.proposed', { supplier: dialog.award.supplierName }),
            )
          }
        />
      ) : null}
      <PasswordDialog
        opened={dialog?.kind === 'acceptCounter'}
        title={k('actions.acceptCounterTitle')}
        body={k('actions.acceptCounterBody')}
        reason="none"
        password={false}
        confirmLabel={k('actions.acceptCounter')}
        onClose={() => setDialog(null)}
        onConfirm={() =>
          dialog
            ? run(
                () => source.acceptCounter(dialog.award.id, { roundNo: dialog.award.roundNo }),
                k('notices.accepted', { supplier: dialog.award.supplierName }),
              )
            : Promise.resolve(null)
        }
      />
      <PasswordDialog
        opened={dialog?.kind === 'finalize'}
        title={k('actions.finalizeTitle')}
        body={k('actions.finalizeBody')}
        reason="none"
        password
        reauthKind="purchase_order"
        confirmLabel={k('actions.finalize')}
        onClose={() => setDialog(null)}
        onConfirm={({ password }) =>
          dialog
            ? run(
                () =>
                  source.finalize(dialog.award.id, {
                    roundNo: dialog.award.roundNo,
                    ...(password ? { password } : {}),
                  }),
                k('notices.finalized', { supplier: dialog.award.supplierName }),
              )
            : Promise.resolve(null)
        }
      />
      <PasswordDialog
        opened={dialog?.kind === 'cancel'}
        title={k('actions.cancelTitle')}
        body={k('actions.cancelBody')}
        reason="required"
        reasonLabel={k('actions.cancelReason')}
        password={false}
        confirmLabel={k('actions.cancel')}
        onClose={() => setDialog(null)}
        onConfirm={({ reason }) =>
          dialog
            ? run(
                () => source.cancel(dialog.award.id, { reason }),
                k('notices.cancelled', { supplier: dialog.award.supplierName }),
              )
            : Promise.resolve(null)
        }
      />
    </div>
  );
}
